import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Globe, CheckCircle, Clock, AlertCircle, Loader2, ArrowRight, MessageCircle } from 'lucide-react';
import { CJMSClient, cjmsApi } from '../../api/cjmsApi';
import { feedbackBus } from '../../services/feedbackBus';

interface ProvisioningStatusProps {
    client: CJMSClient;
}

const PROVISION_STEPS = [
    { id: 'questionnaire', label: 'Questionnaire', desc: 'Business info & design preferences collected', apiAction: 'submitQuestionnaire' as const },
    { id: 'ai_generation', label: 'AI Generation', desc: 'Your website is being built by AI', apiAction: 'generateWebsite' as const },
    { id: 'admin_review', label: 'Admin Review', desc: 'Quality check by our team', apiAction: 'reviewWebsite' as const },
    { id: 'client_review', label: 'Your Review', desc: 'Review and request changes if needed', apiAction: 'approveWebsite' as const },
    { id: 'deploy', label: 'Go Live', desc: 'Deploy to your custom domain', apiAction: 'deployWebsite' as const },
];

export const ProvisioningStatus: React.FC<ProvisioningStatusProps> = ({ client }) => {
    const isEligible = client.currentStage === 'Fulfillment' || client.currentStage === 'Retention' || client.currentStage === 'Customer';
    const [currentStep, setCurrentStep] = useState<string>('questionnaire');
    const [stepStates, setStepStates] = useState<Record<string, 'pending' | 'in_progress' | 'completed' | 'failed'>>({});
    const [runningStep, setRunningStep] = useState<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string>('');
    const [liveUrl, setLiveUrl] = useState<string>('');
    const [errorMsg, setErrorMsg] = useState('');

    // Initialize step states from client data
    useEffect(() => {
        if (client.currentStage === 'Fulfillment' || client.currentStage === 'Retention' || client.currentStage === 'Customer') {
            // Parse provisioning step from notes or default to questionnaire
            const stepMatch = (client as any).notes?.match(/Provisioning step: (\w+)/);
            const activeStep = stepMatch?.[1] || 'questionnaire';
            setCurrentStep(activeStep);

            const states: Record<string, 'pending' | 'in_progress' | 'completed' | 'failed'> = {};
            let foundCurrent = false;
            for (const step of PROVISION_STEPS) {
                if (step.id === activeStep) {
                    states[step.id] = 'in_progress';
                    foundCurrent = true;
                } else if (!foundCurrent) {
                    states[step.id] = 'completed';
                } else {
                    states[step.id] = 'pending';
                }
            }
            setStepStates(states);
        }
    }, [client]);

    if (!isEligible) return null;

    const completedCount = Object.values(stepStates).filter(s => s === 'completed').length;
    const totalCount = PROVISION_STEPS.length;

    const handleStepAction = async (stepId: string) => {
        setRunningStep(stepId);
        setStepStates(prev => ({ ...prev, [stepId]: 'in_progress' }));
        setErrorMsg('');

        try {
            let result: any;

            switch (stepId) {
                case 'questionnaire':
                    // Questionnaire is handled by the Questionnaire component — this is a status display
                    result = await cjmsApi.updateProvisioningStep({ leadId: client.id, email: client.email, step: 'ai_generation' });
                    break;
                case 'ai_generation':
                    result = await cjmsApi.generateWebsite({ leadId: client.id, email: client.email });
                    if (result.success && result.previewUrl) {
                        setPreviewUrl(result.previewUrl);
                    }
                    break;
                case 'admin_review':
                    result = await cjmsApi.reviewWebsite({ leadId: client.id, email: client.email, approved: true, feedback: 'Auto-approved' });
                    break;
                case 'client_review':
                    result = await cjmsApi.approveWebsite({ leadId: client.id, email: client.email, approved: true });
                    break;
                case 'deploy':
                    result = await cjmsApi.deployWebsite({ leadId: client.id, email: client.email });
                    if (result.success && result.liveUrl) {
                        setLiveUrl(result.liveUrl);
                    }
                    break;
            }

            if (result?.success) {
                setStepStates(prev => ({ ...prev, [stepId]: 'completed' }));
                feedbackBus.emit('activity_completed', {
                    activityLabel: `Provisioning step completed: ${stepId}`,
                    clientEmail: client.email,
                });
            } else {
                setStepStates(prev => ({ ...prev, [stepId]: 'failed' }));
                setErrorMsg(result?.error || 'Step failed');
            }
        } catch {
            setStepStates(prev => ({ ...prev, [stepId]: 'failed' }));
            setErrorMsg('Network error');
        } finally {
            setRunningStep(null);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-cyan-600" />
                    <h3 className="text-sm font-bold text-slate-800">Website Provisioning</h3>
                </div>
                <span className="text-[10px] font-medium text-slate-500">{completedCount}/{totalCount} complete</span>
            </div>

            {/* Progress bar */}
            <div className="px-4 pt-4">
                <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(completedCount / totalCount) * 100}%` }}
                        className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"
                    />
                </div>
            </div>

            <div className="p-4 space-y-3">
                {PROVISION_STEPS.map((step, idx) => {
                    const state = stepStates[step.id] || 'pending';
                    return (
                        <div key={step.id} className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                            state === 'completed' ? 'bg-green-50 border-green-200' :
                            state === 'in_progress' ? 'bg-blue-50 border-blue-200' :
                            state === 'failed' ? 'bg-red-50 border-red-200' :
                            'bg-slate-50 border-slate-100'
                        }`}>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    {state === 'completed' && <CheckCircle className="w-4 h-4 text-green-500" />}
                                    {state === 'in_progress' && <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />}
                                    {state === 'failed' && <AlertCircle className="w-4 h-4 text-red-500" />}
                                    {state === 'pending' && <Clock className="w-4 h-4 text-slate-400" />}
                                    <span className={`text-xs font-semibold ${
                                        state === 'completed' ? 'text-green-700' :
                                        state === 'in_progress' ? 'text-blue-700' :
                                        state === 'failed' ? 'text-red-700' : 'text-slate-600'
                                    }`}>{step.label}</span>
                                </div>
                                <p className="text-[10px] text-slate-500 mt-0.5 ml-6">{step.desc}</p>
                                {step.id === 'ai_generation' && previewUrl && state === 'completed' && (
                                    <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="ml-6 text-[10px] text-blue-600 underline">
                                        View Preview →
                                    </a>
                                )}
                                {step.id === 'deploy' && liveUrl && state === 'completed' && (
                                    <a href={liveUrl} target="_blank" rel="noopener noreferrer" className="ml-6 text-[10px] text-green-600 underline font-medium">
                                        Visit Live Site →
                                    </a>
                                )}
                            </div>
                            {state === 'pending' && (
                                <button
                                    onClick={() => handleStepAction(step.id)}
                                    disabled={runningStep !== null}
                                    className="px-3 py-1 text-[10px] font-medium bg-cyan-600 text-white rounded-md hover:bg-cyan-700 transition-colors disabled:opacity-50 shrink-0"
                                >
                                    {runningStep === step.id ? 'Processing...' : 'Start'}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {errorMsg && (
                <div className="px-4 pb-4">
                    <div className="p-2 bg-red-50 rounded-lg border border-red-100">
                        <p className="text-[10px] text-red-600">{errorMsg}</p>
                    </div>
                </div>
            )}

            <div className="px-4 pb-4">
                <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-100">
                    <MessageCircle className="w-3 h-3 text-amber-600" />
                    <p className="text-[9px] text-amber-700">
                        Need help? WhatsApp +27 81 714 2228 — responded within 6-12 hours.
                    </p>
                </div>
            </div>
        </motion.div>
    );
};
